import { useQuery } from "@tanstack/react-query";
import { api } from "../../../lib/api";
import { PageSpinner } from "../../../ui/Spinner";
import {
  Badge,
  EmptyInline,
  Panel,
  SectionTitle,
  fmtDate,
  fmtMoney,
} from "./_ui";

/** Ouvre un PDF servi par une route authentifiée (token Bearer en mémoire). */
async function openAuthenticatedPdf(url: string): Promise<void> {
  const token = window.__victorAdminToken ?? null;
  if (!token) {
    alert("Session expirée. Veuillez vous reconnecter.");
    return;
  }
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    alert(`Échec du PDF (${res.status})`);
    return;
  }
  const objectUrl = URL.createObjectURL(await res.blob());
  window.open(objectUrl, "_blank", "noopener,noreferrer");
  setTimeout(() => URL.revokeObjectURL(objectUrl), 5 * 60 * 1000);
}

const STATUT: Record<
  string,
  { label: string; tone: Parameters<typeof Badge>[0]["tone"] }
> = {
  brouillon: { label: "Brouillon", tone: "slate" },
  envoyee: { label: "Émise", tone: "accent" },
  registered: { label: "Transmise PPF", tone: "emerald" },
  payee: { label: "Payée", tone: "emerald" },
  erreur: { label: "Erreur", tone: "red" },
  annulee: { label: "Annulée", tone: "slate" },
};

export function FacturesHubblotTab({ tenantId }: { tenantId: string }) {
  const q = useQuery({
    queryKey: ["tenant-factures-hubblot", tenantId],
    queryFn: () => api.facturesHubblot.list(tenantId),
  });

  if (q.isLoading) return <PageSpinner />;

  return (
    <Panel padded={false}>
      <div className="px-5 pt-5">
        <SectionTitle
          icon={ICON.receipt}
          desc="Générées par MyVictor à chaque paiement (abonnement, packs). PDF émis en interne ; transmission e-invoicing (B2Brouter) dès qu'elle sera activée."
        >
          Factures MyVictor
        </SectionTitle>
      </div>
      {!q.data || q.data.length === 0 ? (
        <div className="px-5 pb-5">
          <EmptyInline>Aucune facture MyVictor.</EmptyInline>
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-y border-navy-100">
              <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-navy-500">
                Numéro
              </th>
              <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-navy-500">
                Date
              </th>
              <th className="px-5 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-navy-500">
                HT
              </th>
              <th className="px-5 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-navy-500">
                TTC
              </th>
              <th className="px-5 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-navy-500">
                Statut
              </th>
              <th className="px-5 py-3 text-right text-[11px] font-bold uppercase tracking-wider text-navy-500">
                PDF
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-navy-100">
            {q.data.map((f) => {
              const st = STATUT[f.statut] ?? {
                label: f.statut,
                tone: "slate" as const,
              };
              return (
                <tr key={f.id} className="transition-colors hover:bg-navy-50/40">
                  <td className="px-5 py-3 font-bold text-navy-800">{f.numero}</td>
                  <td className="px-5 py-3 text-xs text-navy-600">
                    {fmtDate(f.createdAt)}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums text-navy-700">
                    {fmtMoney(f.montantHt)} €
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums font-semibold text-navy-800">
                    {fmtMoney(f.totalTtc)} €
                  </td>
                  <td className="px-5 py-3">
                    <Badge tone={st.tone} dot>
                      {st.label}
                    </Badge>
                  </td>
                  <td className="px-5 py-3 text-right">
                    <button
                      type="button"
                      onClick={() =>
                        void openAuthenticatedPdf(
                          api.facturesHubblot.pdfUrl(tenantId, f.id),
                        )
                      }
                      className="inline-flex items-center gap-1.5 rounded-xl border border-navy-200 px-3 py-1.5 text-sm font-semibold text-navy-700 transition-colors hover:bg-navy-50"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><path d="M7 10l5 5 5-5" /><path d="M12 15V3" /></svg>
                      PDF
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Panel>
  );
}

const sp = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  viewBox: "0 0 24 24",
};
const ICON = {
  receipt: (
    <svg {...sp}>
      <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1V2l-2 1-2-1-2 1-2-1-2 1-2-1z" />
      <path d="M8 7h8M8 11h8M8 15h5" />
    </svg>
  ),
};
